import React, { useRef, useEffect, useState } from 'react';
import { useCssVariableValue } from "../../../hooks";
import './Loader.scss';

type LoaderProps = {
  progress?: number;
  size?: "small" | "large";
  variant?: "primary" | "secondary";
  label?: string;
};

export const Loader = (
  {
    progress = 0,
    size = "large",
    variant = "primary",
    label,
  }: LoaderProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(300);

  const height = size === 'large' ? 48 : 32;

  // Colors from CSS vars
  const primaryActive = useCssVariableValue('--primary');
  const secondaryActive = useCssVariableValue('--secondary-highlight');
  const active = variant === "primary" ? primaryActive : secondaryActive;
  const primaryMuted = useCssVariableValue('--primary-muted');
  const secondaryMuted = useCssVariableValue('--secondary');
  const muted = variant === "primary" ? primaryMuted : secondaryMuted;
  const highlight = useCssVariableValue('--on-background');


  // Keep track of current animated progress and target progress separately
  const currentProgressRef = useRef(Math.max(0, Math.min(100, progress)));
  const targetProgressRef = useRef(progress);

  // Wave animation state persists across renders
  const waveRef = useRef({
    amplitude: size === 'large' ? 8 : 4,
    wavelength: 60,
    phase: 0,
    speed: 0.06,
  });

  // Update target progress when prop changes (without restarting animation)
  useEffect(() => {
    targetProgressRef.current = Math.max(0, Math.min(100, progress));
  }, [progress]);

  // Observe container width
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Draw animation (runs continuously, no restart on progress change)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    let animationId: number;

    const drawWave = () => {
      ctx.clearRect(0, 0, width, height);

      // Smoothly animate toward target progress
      currentProgressRef.current +=
        (targetProgressRef.current - currentProgressRef.current) * 0.1;

      const progressWidth = (currentProgressRef.current / 100) * width;
      const wave = waveRef.current;

      const lineWidth = size === 'large' ? 6 : 3;
      const padding = lineWidth / 2 + 1;
      const gap = 6;

      // Remaining line
      const startX = progressWidth + gap;
      const endX = width - padding * 2;
      if (startX < endX) {
        ctx.beginPath();
        ctx.moveTo(startX, height / 2);
        ctx.lineTo(endX, height / 2);
        ctx.strokeStyle = muted;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      // Dot at the end
      ctx.beginPath();
      ctx.arc(width - padding - lineWidth / 2, height / 2, lineWidth * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = highlight;
      ctx.fill();

      // Wave
      ctx.beginPath();
      ctx.moveTo(padding, height / 2);
      for (let x = 0; x <= progressWidth - gap - padding; x++) {
        const fade = Math.min(
          x / wave.wavelength,
          (progressWidth - gap - padding - x) / wave.wavelength,
          1
        );
        const y =
          height / 2 +
          wave.amplitude *
          fade *
          Math.sin((x / wave.wavelength) * 2 * Math.PI + wave.phase);
        ctx.lineTo(padding + x, y);
      }
      ctx.strokeStyle = active;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Keep wave moving
      wave.phase += wave.speed;

      animationId = requestAnimationFrame(drawWave);
    };

    animationId = requestAnimationFrame(drawWave);
    return () => cancelAnimationFrame(animationId);
  }, [size, width, height, active, muted, highlight]);

  return (
    <div ref={containerRef} className="loader" aria-label={label}>
      <canvas
        ref={canvasRef}
        style={{
          borderRadius: height / 2,
          background: 'transparent',
          display: 'block',
        }}
      />
      <div className="info">
        <span className="percentage">{progress}%</span>
        { label && <span className="label">{label}</span> }
      </div>
    </div>
  );
};
